import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { CreditCard } from 'lucide-react';
import { uploadFileWithMetadata } from '../../../storageService';

// Reusa validaciones del proyecto
const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'];

const SignupDocumentsStep = ({ formData, updateFormData, errors }) => {
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);

    // Estado para el toggle entre DNI y CUIT - SIEMPRE al inicio
    const [documentType, setDocumentType] = React.useState(
        (formData.cuit && formData.cuit.trim() !== '') ? 'cuit' : 'dni'
    );

    const isService = formData.type === 'service';

    // Función para verificar si se pueden cargar fotos
    const canUploadPhotos = () => {
        // No permitir carga de fotos si hay errores en el username o si está vacío
        return formData.username && formData.username.trim() !== '' && !errors.username;
    };

    // Función para manejar clics en botones deshabilitados
    const handleDisabledClick = () => {
        if (!formData.username || formData.username.trim() === '') {
            toast.warning('Primero debes completar tu nombre de usuario en el paso "Información Básica"');
        } else if (errors.username) {
            toast.warning('Hay un problema con tu nombre de usuario. Por favor corrígelo antes de cargar fotos');
        }
    };

    // Cleanup de URLs cuando se desmonta el componente - SIEMPRE debe estar aquí
    React.useEffect(() => {
        return () => {
            if (documentPreviewUrl) {
                URL.revokeObjectURL(documentPreviewUrl);
            }
        };
    }, [documentPreviewUrl]);

    // Actualizar el tipo de documento cuando cambie el formData - SIEMPRE aquí
    React.useEffect(() => {
        if (formData.cuit && formData.cuit.trim() !== '' && documentType === 'dni') {
            setDocumentType('cuit');
        }
    }, [formData.cuit, documentType]);

    if (!isService) {
        return (
            <div className="step-content-wrapper">
                <div className="step-header">
                    <h2>Documentación</h2>
                    <p>No se requiere documentación adicional para este tipo de proveedor</p>
                </div>
            </div>
        );
    }

    const validateFile = (file) => {
        if (!file) return false;
        if (file.size > MAX_FILE_SIZE) {
            toast.error(`El archivo ${file.name} supera el límite de 8MB.`);
            return false;
        }
        if (!VALID_IMAGE_TYPES.includes(file.type)) {
            toast.error(`El archivo ${file.name} no es de un formato de imagen válido.`);
            return false;
        }
        return true;
    };

    // Función para crear URL de vista previa
    const createImagePreview = (file) => {
        if (file) {
            return URL.createObjectURL(file);
        }
        return null;
    };

    const handleDocumentPhotoChange = async (file) => {
        if (!file || !validateFile(file)) return;

        // Verificar que se pueda cargar fotos (username válido)
        if (!canUploadPhotos()) {
            toast.error('Primero debes completar un nombre de usuario válido');
            return;
        }

        // Crear preview inmediatamente
        const previewUrl = createImagePreview(file);
        setDocumentPreviewUrl(previewUrl);

        setUploadingDoc(true);
        try {
            const docPath = `providers/service/${formData.username}/document.jpg`;
            await uploadFileWithMetadata(file, docPath);
            updateFormData('documentPhoto', docPath);
            toast.success('Foto del documento subida con éxito');
        } catch (e) {
            console.error('Error al subir foto del documento', e);
            toast.error('No se pudo subir la foto del documento');
            // Limpiar preview si hay error
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
                setDocumentPreviewUrl(null);
            }
        } finally {
            setUploadingDoc(false);
        }
    };

    const handleOnlyNumbers = (value, maxLen) => value.replace(/\D/g, '').slice(0, maxLen);


    const handleDocumentTypeToggle = (type) => {
        setDocumentType(type);
        // Limpiar el campo opuesto cuando se cambie el tipo
        if (type === 'dni') {
            updateFormData('cuit', '');
        } else {
            updateFormData('document', '');
        }
    };

    const handleDocumentChange = (value) => {
        if (documentType === 'dni') {
            updateFormData('document', handleOnlyNumbers(value, 8));
        } else {
            updateFormData('cuit', handleOnlyNumbers(value, 11));
        }
    };

    return (
        <div className="step-content-wrapper">
            <div className="step-header">
                <h2>Documentación</h2>
                <p>Validamos tu identidad para dar mayor confianza a tus clientes. Estos datos no serán visibles en la plataforma.</p>
            </div>

            <div className="form-section">
                <div className="form-group">
                    <label htmlFor="document">
                        {documentType === 'dni' ? 'Número de documento (DNI)' : 'CUIT o CUIL'}
                        <span className="required">*</span>
                    </label>

                    {/* Toggle para elegir entre DNI y CUIT */}
                    <div className="document-type-toggle" style={{ marginBottom: '8px' }}>
                        <button
                            type="button"
                            className={`toggle-btn ${documentType === 'dni' ? 'active' : ''}`}
                            onClick={() => handleDocumentTypeToggle('dni')}
                            style={{
                                padding: '6px 12px',
                                margin: '0 4px 0 0',
                                border: '1px solid var(--input-border-dark)',
                                borderRadius: '4px',
                                background: documentType === 'dni' ? 'var(--primary-color)' : 'transparent',
                                color: documentType === 'dni' ? 'white' : 'var(--text-color)',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            DNI
                        </button>
                        <button
                            type="button"
                            className={`toggle-btn ${documentType === 'cuit' ? 'active' : ''}`}
                            onClick={() => handleDocumentTypeToggle('cuit')}
                            style={{
                                padding: '6px 12px',
                                margin: '0',
                                border: '1px solid var(--input-border-dark)',
                                borderRadius: '4px',
                                background: documentType === 'cuit' ? 'var(--primary-color)' : 'transparent',
                                color: documentType === 'cuit' ? 'white' : 'var(--text-color)',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            CUIT/CUIL
                        </button>
                    </div>

                    <div className="field-help">
                        {documentType === 'dni'
                            ? 'Solo números, exactamente 8 dígitos.'
                            : 'Solo números, exactamente 11 dígitos.'}
                    </div>
                    <input
                        id="document"
                        type="tel"
                        className={`form-input ${(documentType === 'dni' ? errors.document : errors.cuit) ? 'error' : ''}`}
                        placeholder={documentType === 'dni' ? '8 dígitos' : '11 dígitos'}
                        value={documentType === 'dni' ? (formData.document || '') : (formData.cuit || '')}
                        onChange={(e) => handleDocumentChange(e.target.value)}
                        maxLength={documentType === 'dni' ? 8 : 11}
                    />
                    <div className="field-footer">
                        {documentType === 'dni' && errors.document && <span className="error-message">{errors.document}</span>}
                        {documentType === 'cuit' && errors.cuit && <span className="error-message">{errors.cuit}</span>}
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="documentName">Nombre completo <span className="required">*</span></label>
                    <div className="field-help">Solo letras y espacios</div>
                    <input
                        id="documentName"
                        type="text"
                        className={`form-input ${errors.documentName ? 'error' : ''}`}
                        placeholder="Tal como figura en tu documento"
                        value={formData.documentName || ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (/^[a-zA-ZÁÉÍÓÚáéíóúñÑ\s]*$/.test(val)) {
                                updateFormData('documentName', val);
                            }
                        }}
                        maxLength={80}
                    />
                    <div className="field-footer">
                        {errors.documentName && <span className="error-message">{errors.documentName}</span>}
                    </div>
                </div>

                {/* SE DESHABILITA TEMPORALMENTE LA CARGA DE DNI
          <div className="form-group">
            <label>Foto del frente del documento <span className="required">*</span></label>
            <div className="field-help">Formatos: JPG, PNG, GIF, WebP, SVG, BMP. Máx 8MB</div>
            <div className="photo-upload-area">
              {formData.documentPhoto && documentPreviewUrl ? (
                  <div className="document-photo-preview">
                    <img
                        src={documentPreviewUrl}
                        alt="Vista previa del documento"
                        style={{
                          width: '200px',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '2px solid #ddd',
                          marginBottom: '10px'
                        }}
                    />
                    <div className="photo-actions">
                      <label className="upload-btn">
                        {uploadingDoc ? 'Subiendo...' : 'Cambiar foto'}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleDocumentPhotoChange(e.target.files[0])}
                            disabled={uploadingDoc}
                            style={{ display: 'none' }}
                        />
                      </label>
                    </div>
                  </div>
              ) : formData.documentPhoto ? (
                  <div className="upload-placeholder">
                    <p>Documento cargado</p>
                    <label className="upload-btn">
                      {uploadingDoc ? 'Subiendo...' : 'Cambiar foto'}
                      <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleDocumentPhotoChange(e.target.files[0])}
                          disabled={uploadingDoc}
                          style={{ display: 'none' }}
                      />
                    </label>
                  </div>
              ) : (
                  <div className="upload-placeholder">
                    <div className="upload-icon"><CreditCard size={40} /></div>
                    <p>Sube una foto clara del frente de tu documento</p>
                    <label className="upload-btn primary">
                      {uploadingDoc ? 'Subiendo...' : 'Seleccionar foto'}
                      <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleDocumentPhotoChange(e.target.files[0])}
                          disabled={uploadingDoc}
                          style={{ display: 'none' }}
                      />
                    </label>
                  </div>
              )}
            </div>
            <div className="field-footer">
              {errors.documentPhoto && <span className="error-message">{errors.documentPhoto}</span>}
            </div>
          </div>
          */}
            </div>
        </div>
    );
};

export default SignupDocumentsStep;
